import { Link } from 'react-router-dom';
import { Instagram, Mail, MapPin } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { ZeroSmokeLogoFull } from './Logo';
import { brands } from '@shared/brands';
import { cities } from '@shared/cities';

export default function Footer() {
  const { isDark } = useTheme();

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer className={`transition-colors duration-300 ${isDark ? 'bg-gray-950 text-gray-400' : 'bg-gray-950 text-gray-400'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <ZeroSmokeLogoFull iconSize={34} dark />
            <p className="text-sm leading-relaxed">
              Your trusted destination for premium, smoke-free nicotine alternatives in Country. 
              Clean nicotine. No tobacco. No smoke. Just the hit.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.instagram.com/zerosmoke.in_2026/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-emerald-600 transition-colors"
              >
                <Instagram size={18} />
              </a>
              <a
                href="mailto:zerosmoke2026@gmail.com"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-emerald-600 transition-colors"
              >
                <Mail size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { to: '/', label: 'Home' },
                { to: '/store', label: 'Store' },
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact Us' },
                { to: '/cart', label: 'Cart' },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    onClick={scrollTop}
                    className="text-sm hover:text-emerald-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Brands */}
          <div>
            <h4 className="text-white font-semibold mb-4">Our Brands</h4>
            <ul className="space-y-3">
              {brands.map((b) => (
                <li key={b.slug}>
                  <Link
                    to={`/${b.slug}`}
                    onClick={scrollTop}
                    className="text-sm hover:text-emerald-400 transition-colors"
                  >
                    {b.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Policies */}
          <div>
            <h4 className="text-white font-semibold mb-4">Policies</h4>
            <ul className="space-y-3">
              {[
                { to: '/policies/refund', label: 'Refund Policy' },
                { to: '/policies/shipping', label: 'Shipping Policy' },
                { to: '/policies/terms', label: 'Terms of Service' },
                { to: '/policies/privacy', label: 'Privacy Policy' },
              ].map((policy) => (
                <li key={policy.to}>
                  <Link
                    to={policy.to}
                    onClick={scrollTop}
                    className="text-sm hover:text-emerald-400 transition-colors"
                  >
                    {policy.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex items-start gap-2 text-sm">
              <MapPin size={16} className="flex-shrink-0 mt-0.5" />
              <span>Country-wide delivery</span>
            </div>
          </div>
        </div>

        {/* Delivery Cities — show 15 featured cities + more link to /city index */}
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-800">
          <div className="flex items-baseline gap-3 flex-wrap mb-3">
            <h4 className="text-white font-semibold text-sm">We ship across Country</h4>
            <span className="text-xs text-gray-500">Featured city guides:</span>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-2 items-center">
            {[...cities]
              .sort((a, b) => a.name.localeCompare(b.name))
              .slice(0, 15)
              .map((c) => (
                <span key={c.slug} className="flex items-center gap-3">
                  <Link
                    to={`/city/${c.slug}`}
                    onClick={scrollTop}
                    className="text-sm hover:text-emerald-400 transition-colors"
                  >
                    {c.name}
                  </Link>
                  <span className="text-gray-700">·</span>
                </span>
              ))}
            <Link
              to="/city"
              onClick={scrollTop}
              className="text-sm font-medium text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              more ({cities.length} cities) →
            </Link>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm">Copyright © 2026 Zerosmoke.in. All Rights Reserved.</p>
          <p className="text-xs text-gray-500">
            This product contains nicotine. Nicotine is an addictive chemical. For adults only (18+).
          </p>
        </div>
      </div>
    </footer>
  );
}
